import { SetStateAction, useEffect, useState } from "react";
import { QuationProps } from "../../store/actionType";
import HandleUpdateQuestion from "../HandleUpdateQuestion";
import { VideoDetailProps } from "modules/video/video_detail/store/actionType";
import { TrashIcon } from "assets";
import { CustomTooltip, SmallScreenModel } from "components";
import HandleDeleteQuestion from "../HandleDeleteQuestion";
import { connect, useSelector } from "react-redux";
import { updateQuestionStatus } from "../../store/action";

interface OwnProps {
  questionData: QuationProps[];
  videoData: VideoDetailProps;
  onClose: () => void;
  setMode: (mode: "add" | "preview") => void;
}
interface Props extends OwnProps {
  onUpdateQuestionStatus: (questionId: string, isPublished: boolean) => void;
}
const PreviewQandA: React.FC<Props> = ({
  questionData,
  videoData,
  onClose,
  onUpdateQuestionStatus,
  setMode
}) => {
  const reduxQuestions = useSelector(
    (state: any) => state.questions?.questionsState?.questions || []
  );
  const [questions, setQuestions] = useState<QuationProps[]>(reduxQuestions);

  const [openEdit, setOpenEdit] = useState<string | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const selectedQuestion = questions.find((q) => q.id.toString() === openEdit);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
  const togglePublish = (id: string) => {
    const question = questions.find((q) => String(q.id) === id);
    if (!question) return;

    const action = question.is_published ? "mark as Draft" : "publish";
    const confirmed = window.confirm(
      `Are you sure you want to ${action} this question?`
    );
    if (!confirmed) return;

    onUpdateQuestionStatus(id, !question.is_published);
  };

  useEffect(() => {
    setQuestions(reduxQuestions);
  }, [reduxQuestions]);

  if (questions.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500 border rounded-lg bg-gray-50">
        No questions available to preview.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {openEdit ? (
        <HandleUpdateQuestion
          onClose={onClose}
          videoData={videoData}
          questionId={openEdit}
          questionData={selectedQuestion ? [selectedQuestion] : []}
          setMode={setMode}
        />
      ) : (
        <>
          {questions.map((q, index) => (
            <div
              key={String(q.id)}
              className="border rounded-xl shadow-sm bg-white p-6 hover:shadow-md transition"
            >
              {/* Question Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-bold text-lg">
                  Q{index + 1}. {q.question}
                </h2>
                <div className="flex items-center gap-2">
                  <CustomTooltip title="Change Visibility" placement="left">
                    <span
                      onClick={() => togglePublish(String(q.id))}
                      className={`cursor-pointer text-xs px-2 py-1 rounded hover:shadow-md ${
                        q.is_published
                          ? "bg-primary-300 text-primary-500"
                          : "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {q.is_published ? "Published" : "Draft"}
                    </span>
                  </CustomTooltip>
                  <div className="border px-2 py-1 text-xs rounded-md hover:border-primary-300 hover:text-primary-700 hover:shadow-md">
                    <button onClick={() => setOpenEdit(String(q.id))}>
                      Edit
                    </button>
                  </div>
                  <div className="border px-2 py-1 text-xs rounded-md hover:border-primary-300 hover:text-primary-700 hover:shadow-md">
                    <button
                      onClick={() => {
                        setSelectedDeleteId(String(q.id));
                        setOpenDelete(true);
                      }}
                      className="flex items-center"
                    >
                      <TrashIcon className="size-4 mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </div>
              {openDelete && selectedDeleteId && (
                <SmallScreenModel
                  showModal={openDelete}
                  setShowModal={setOpenDelete}
                >
                  <HandleDeleteQuestion
                    questionId={selectedDeleteId}
                    closeModal={() => setOpenDelete(false)}
                  />
                </SmallScreenModel>
              )}

              {/* Options */}
              <ul className="space-y-2">
                {q.options.map((opt) => (
                  <li
                    key={String(opt.id)}
                    className={`flex items-center justify-between p-3 rounded border ${
                      opt.is_correct_answer
                        ? "bg-primary-300 border-primary-400"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <span>{opt.option}</span>
                    {opt.is_correct_answer && (
                      <span className="text-primary-500 font-medium text-sm">
                        ✔ Correct
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              {/* Footer */}
              <div className="mt-4 text-xs text-gray-400 flex justify-between">
                <span>Video ID: {q.video_id}</span>
                <span>Question ID: {String(q.id)}</span>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => ({
  onUpdateQuestionStatus: (questionId: string, isPublished: boolean) =>
    dispatch(updateQuestionStatus(questionId, isPublished)),
});

export default connect(null, mapDispatchToProps)(PreviewQandA);
